"use client";

import { useState, useEffect } from "react";
import { useTheme } from "next-themes";
import Link from "next/link";
import Image from "next/image";
import { Menu, X, ChevronDown, Wallet, CalendarCheck, UserPlus, Database, TrendingUp, BarChart3 } from "lucide-react";
import ModeToggle from "@/components/shared/ui/ModeToggle";
import ModulesDropdown from "@/components/Website/Navbar/";

const NAV_LINKS = [
  { label: "Features", href: "/features" },
  { label: "Pricing", href: "/pricing" },
  { label: "Contact", href: "/contact" },
];

const MODULES = [
  { label: "Payroll & Compliance", href: "/modules/payroll-compliance", icon: Wallet },
  { label: "Attendance & Leave Management", href: "/modules/attendance-leave", icon: CalendarCheck },
  { label: "Hiring & Onboarding", href: "/modules/hiring-onboarding", icon: UserPlus },
  { label: "Employee Database & HR Reporting", href: "/modules/employee-database", icon: Database },
  { label: "Performance & Engagement", href: "/modules/performance-engagement", icon: TrendingUp },
  { label: "Analytics & Dashboards", href: "/modules/analytics-dashboards", icon: BarChart3 },
];

export default function Navbar() {
  const { theme } = useTheme();
  const [mounted, setMounted] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [mobileModulesOpen, setMobileModulesOpen] = useState(false);

  useEffect(() => {
    setMounted(true);
    const onScroll = () => setScrolled(window.scrollY > 12);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const logo =
    theme === "dark"
      ? "/logo/aeronpay-hrms-logo-white.png"
      : "/logo/aeronpay-hrms-logo-dark.png";

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled
          ? "bg-background/90 backdrop-blur-md border-b border-border"
          : "bg-transparent border-b border-transparent"
        }`}
    >
      <nav className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
        <Link href="/" className="flex items-center gap-2">
          <div className="relative w-[180px] h-[60px]">
            {mounted && (
              <Image
                src={logo}
                alt="AeronPay HRMS"
                fill
                priority
                className="object-contain object-left"
              />
            )}
          </div>
        </Link>

        <div className="hidden md:flex items-center gap-8">
          <Link
            href={NAV_LINKS[0].href}
            className="text-sm text-muted-foreground hover:text-foreground transition-colors"
          >
            {NAV_LINKS[0].label}
          </Link>

          <ModulesDropdown />

          {NAV_LINKS.slice(1).map((link) => (
            <Link
              key={link.href}
              href={link.href}
              className="text-sm text-muted-foreground hover:text-foreground transition-colors"
            >
              {link.label}
            </Link>
          ))}
        </div>

        <div className="hidden md:flex items-center gap-3">
          <ModeToggle />
          <Link
            href="/login"
            className="text-sm font-medium text-foreground hover:text-primary transition-colors px-3 py-2"
          >
            Sign in
          </Link>
          <Link
            href="/book-demo"
            className="text-sm font-medium bg-primary text-primary-foreground rounded-lg px-4 py-2 shadow-[0_1px_0_0_rgba(255,255,255,0.15)_inset,0_4px_12px_-4px_hsl(217_91%_60%/0.5)] hover:shadow-[0_1px_0_0_rgba(255,255,255,0.15)_inset,0_6px_16px_-4px_hsl(217_91%_60%/0.65)] hover:-translate-y-0.5 transition-all duration-200"
          >
            Book a demo
          </Link>
        </div>

        <button
          className="md:hidden text-foreground"
          onClick={() => setMobileOpen(!mobileOpen)}
          aria-label="Toggle menu"
        >
          {mobileOpen ? <X size={22} /> : <Menu size={22} />}
        </button>
      </nav>

      {mobileOpen && (
        <div className="md:hidden bg-background border-t border-border px-6 py-4 space-y-1 max-h-[80vh] overflow-y-auto">
          <Link
            href={NAV_LINKS[0].href}
            className="block text-sm text-foreground py-2"
            onClick={() => setMobileOpen(false)}
          >
            {NAV_LINKS[0].label}
          </Link>

          <button
            className="w-full flex items-center justify-between text-sm text-foreground py-2"
            onClick={() => setMobileModulesOpen(!mobileModulesOpen)}
            aria-expanded={mobileModulesOpen}
          >
            Modules
            <ChevronDown
              size={16}
              className={`transition-transform duration-200 ${mobileModulesOpen ? "rotate-180" : ""}`}
            />
          </button>
          {mobileModulesOpen && (
            <div className="pl-3 border-l border-border ml-1 space-y-1 pb-2">
              {MODULES.map((mod) => {
                const Icon = mod.icon;
                return (
                  <Link
                    key={mod.href}
                    href={mod.href}
                    className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground py-2"
                    onClick={() => {
                      setMobileOpen(false);
                      setMobileModulesOpen(false);
                    }}
                  >
                    <Icon size={15} />
                    {mod.label}
                  </Link>
                );
              })}
            </div>
          )}

          {NAV_LINKS.slice(1).map((link) => (
            <Link
              key={link.href}
              href={link.href}
              className="block text-sm text-foreground py-2"
              onClick={() => setMobileOpen(false)}
            >
              {link.label}
            </Link>
          ))}

          <div className="flex flex-col gap-2 pt-3">
            <Link href="/login" className="text-sm font-medium text-foreground">
              Sign in
            </Link>
            <Link
              href="/book-demo"
              className="text-sm font-medium bg-primary text-primary-foreground rounded-lg px-4 py-2 shadow-[0_1px_0_0_rgba(255,255,255,0.15)_inset,0_4px_12px_-4px_hsl(217_91%_60%/0.5)] hover:shadow-[0_1px_0_0_rgba(255,255,255,0.15)_inset,0_6px_16px_-4px_hsl(217_91%_60%/0.65)] hover:-translate-y-0.5 transition-all duration-200"
            >
              Book a demo
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}